import React, { useMemo } from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const SALES_COLOR = "#5D38DB";
const LITRES_COLOR = "#0D9488";
const TREND_COLORS = [
  "#5D38DB",
  "#2563EB",
  "#0D9488",
  "#D97706",
  "#7C3AED",
  "#64748B",
];

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const LeaderboardTooltip = ({ active, payload, metricLabel, isLitres }) => {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{row.name}</p>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">{metricLabel}</span>
        <span className="owner-chart-tooltip__value">
          {isLitres ? formatLitres(row.value) : formatCurrency(row.value)}
        </span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Fuel</span>
        <span className="owner-chart-tooltip__value">{row.fuel || "—"}</span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Tank</span>
        <span className="owner-chart-tooltip__value">{row.tank || "—"}</span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Active days</span>
        <span className="owner-chart-tooltip__value">{row.days_active || 0}</span>
      </div>
    </div>
  );
};

const LeaderboardChart = ({ data, color, metricLabel, isLitres, emptyMessage }) => {
  if (!data.length) {
    return (
      <div className="owner-chart-empty owner-chart-empty--compact">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={Math.max(260, data.length * 46)}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={isLitres ? formatLitres : formatCompact}
          tick={{ fill: "#64748b", fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={130}
          tick={{ fill: "#334155", fontSize: 11 }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          content={
            <LeaderboardTooltip metricLabel={metricLabel} isLitres={isLitres} />
          }
        />
        <Bar dataKey="value" name={metricLabel} radius={[0, 6, 6, 0]} barSize={22}>
          {data.map((entry, index) => (
            <Cell
              key={entry.nozzle_id || entry.name}
              fill={index === 0 ? color : TREND_COLORS[index % TREND_COLORS.length]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const OwnerNozzleAnalyticsChart = ({
  data = [],
  series = [],
  summary = {},
  topBySales = [],
  topByLitres = [],
  byShare = [],
  nozzles = [],
  loading = false,
}) => {
  const shareData = useMemo(
    () =>
      byShare
        .filter((item) => item.amount > 0)
        .map((item, index) => ({
          name: item.name,
          value: item.amount,
          pct: item.pct,
          fill: TREND_COLORS[index % TREND_COLORS.length],
        })),
    [byShare]
  );

  const areaSeries = useMemo(
    () => series.filter((item) => item.type !== "nozzle_other"),
    [series]
  );
  const othersSeries = useMemo(
    () => series.find((item) => item.key === "Others"),
    [series]
  );

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!nozzles.length && !summary?.total_sales) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-tachometer owner-chart-empty__icon" aria-hidden="true" />
        <p>No nozzle sales data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Nozzle sales</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.total_sales)}
            </strong>
            <small className="owner-sales-kpi__hint">
              {summary?.active_nozzles || 0} active nozzles
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Total litres</span>
            <strong className="owner-sales-kpi__value">
              {formatLitres(summary?.total_litres)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--teal">
            <span className="owner-sales-kpi__label">Top nozzle</span>
            <strong className="owner-sales-kpi__value">
              {summary?.top_nozzle || "—"}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatCurrency(summary?.top_nozzle_sales)} ·{" "}
              {formatLitres(summary?.top_nozzle_litres)}
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Avg per nozzle</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.avg_sales_per_nozzle)}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatLitres(summary?.avg_litres_per_day)} / active day
            </small>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">
              Sales trend by nozzle (top 5)
            </p>
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
                <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  tickFormatter={formatCompact}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip content={<TrendTooltip />} />
                <Legend />

                {areaSeries.map((item, index) => (
                  <Area
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.label}
                    stackId="nozzle"
                    stroke={TREND_COLORS[index % TREND_COLORS.length]}
                    fill={TREND_COLORS[index % TREND_COLORS.length]}
                    fillOpacity={0.35}
                    strokeWidth={2}
                    connectNulls
                  />
                ))}

                {othersSeries ? (
                  <Area
                    type="monotone"
                    dataKey={othersSeries.key}
                    name={othersSeries.label}
                    stackId="nozzle"
                    stroke="#94A3B8"
                    fill="#94A3B8"
                    fillOpacity={0.25}
                    strokeWidth={2}
                    connectNulls
                  />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="owner-chart-panel owner-chart-panel--compact h-100">
            <p className="owner-chart-panel__subtitle mb-2">Sales share by nozzle</p>
            {shareData.length ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={shareData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={88}
                      paddingAngle={2}
                    >
                      {shareData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        formatCurrency(value),
                        `${name} (${formatPercent(props?.payload?.pct)})`,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="owner-chart-legend mt-2">
                  {shareData.slice(0, 6).map((item) => (
                    <div className="owner-chart-legend__item" key={item.name}>
                      <span
                        className="owner-chart-legend__swatch"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span>
                        {item.name} ({formatPercent(item.pct)})
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="owner-chart-empty owner-chart-empty--compact">
                <p>No nozzle share data for this period.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">Top nozzles by sales</p>
            <LeaderboardChart
              data={topBySales}
              color={SALES_COLOR}
              metricLabel="Sales"
              isLitres={false}
              emptyMessage="No nozzle sales in this period."
            />
          </div>
        </div>
        <div className="col-lg-6">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">Top nozzles by litres</p>
            <LeaderboardChart
              data={topByLitres}
              color={LITRES_COLOR}
              metricLabel="Litres"
              isLitres
              emptyMessage="No nozzle volume in this period."
            />
          </div>
        </div>
      </div>

      {nozzles.length > 0 ? (
        <div className="owner-chart-panel">
          <p className="owner-chart-panel__subtitle mb-2">Nozzle detail</p>
          <div className="table-responsive">
            <table className="table table-sm owner-employee-table mb-0">
              <thead>
                <tr>
                  <th>Nozzle</th>
                  <th>Tank</th>
                  <th>Fuel</th>
                  <th className="text-end">Sales</th>
                  <th className="text-end">Litres</th>
                  <th className="text-end">Days</th>
                  <th className="text-end">Avg/day</th>
                  <th className="text-end">Employees</th>
                </tr>
              </thead>
              <tbody>
                {nozzles.map((row) => (
                  <tr key={row.nozzle_id}>
                    <td>{row.name}</td>
                    <td>{row.tank}</td>
                    <td>{row.fuel}</td>
                    <td className="text-end">{formatCurrency(row.sales)}</td>
                    <td className="text-end">{formatLitres(row.litres)}</td>
                    <td className="text-end">{row.days_active}</td>
                    <td className="text-end">{formatCurrency(row.avg_sales_per_day)}</td>
                    <td className="text-end">{row.employees_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OwnerNozzleAnalyticsChart;
